// src/theme.ts
import { z } from "zod";
var DESIGN_TOKENS = {
  green: "#00704A",
  greenDeep: "#004C33",
  greenInk: "#042B1D",
  greenNight: "#02160F",
  cream: "#FAF7F0",
  creamDeep: "#F2EDE2",
  gold: "#C5A572",
  goldLight: "#E8DCC4",
  goldDark: "#A68B5B",
  ink: "#171717",
  inkSoft: "#4F564F",
  white: "#FFFFFF",
  plateYellow: "#FCD116",
  plateBlue: "#003399",
  danger: "#B42318",
  success: "#059669",
  displayFont: '"Fraunces", Georgia, serif',
  bodyFont: '"Archivo", -apple-system, sans-serif',
  fontHref: "https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400&display=swap"
};
var themeConfigSchema = z.object({
  /** Primary brand colour — CTAs, progress, accents. */
  primary: z.string().default(DESIGN_TOKENS.green),
  primaryDark: z.string().default(DESIGN_TOKENS.greenDeep),
  accent: z.string().default(DESIGN_TOKENS.gold),
  background: z.string().default(DESIGN_TOKENS.cream),
  surface: z.string().default(DESIGN_TOKENS.white),
  ink: z.string().default(DESIGN_TOKENS.ink),
  inkSoft: z.string().default(DESIGN_TOKENS.inkSoft),
  /** Border radius (px) applied to cards/inputs. */
  radius: z.number().int().min(0).max(40).default(16),
  logoUrl: z.string().url().optional(),
  displayFont: z.string().default(DESIGN_TOKENS.displayFont),
  bodyFont: z.string().default(DESIGN_TOKENS.bodyFont)
});
var DEFAULT_THEME = themeConfigSchema.parse({});

// src/copy.ts
import { z as z2 } from "zod";
var copyConfigSchema = z2.object({
  headline: z2.string().default("Book your car in. In under a minute."),
  subheadline: z2.string().default(
    "Enter your reg, pick a service, choose a time. No phone calls, no waiting."
  ),
  ctaQuote: z2.string().default("Book now"),
  ctaBook: z2.string().default("Book now"),
  ctaContinue: z2.string().default("Continue"),
  ctaConfirm: z2.string().default("Confirm booking"),
  registrationLabel: z2.string().default("Enter your registration"),
  registrationHelp: z2.string().default("I don't know my reg"),
  vehicleNotFoundTitle: z2.string().default("We couldn't find that one \u2014 enter your details"),
  postcodeLabel: z2.string().default("Your postcode"),
  useLocationLabel: z2.string().default("Use my current location"),
  contactTitle: z2.string().default("Where should we send your quote?"),
  motTitle: z2.string().default("Book your MOT"),
  servicesTitle: z2.string().default("Add a service to your MOT?"),
  otherServicesLabel: z2.string().default("Other services"),
  skipServicesLabel: z2.string().default("Skip \u2014 just the MOT"),
  datetimeTitle: z2.string().default("Pick a date and time"),
  detailsTitle: z2.string().default("Almost done \u2014 your details"),
  summaryTitle: z2.string().default("Review your booking"),
  confirmationTitle: z2.string().default("You're booked in"),
  noSlotsMessage: z2.string().default(
    "No online slots for this day. Try another date \u2014 or leave your number and we'll call you with the soonest opening."
  ),
  callbackCta: z2.string().default("Request a callback instead")
});
var DEFAULT_COPY = copyConfigSchema.parse({});

// src/vehicle.ts
import { z as z3 } from "zod";
var fuelTypeSchema = z3.enum([
  "Petrol",
  "Diesel",
  "Hybrid",
  "Electric",
  "Other"
]);
var vehicleSchema = z3.object({
  registration: z3.string().min(1),
  make: z3.string().min(1),
  /** May be empty — the DVLA enquiry service doesn't expose a model. */
  model: z3.string(),
  fuel: fuelTypeSchema,
  year: z3.number().int().min(1900).max(2100),
  colour: z3.string().optional(),
  engineSize: z3.string().optional(),
  bodyType: z3.string().optional(),
  /** ISO date the current MOT runs out (from DVLA/DVSA), when known. */
  motExpiryDate: z3.string().optional()
});
var manualVehicleSchema = z3.object({
  make: z3.string().min(1),
  model: z3.string().min(1),
  fuel: fuelTypeSchema,
  year: z3.number().int().min(1900).max(2100)
});
var garageHiveVehicleSchema = z3.object({
  registration: z3.string().min(1),
  make: z3.string().min(1),
  model: z3.string()
});
var anyVehicleSchema = z3.union([
  vehicleSchema,
  garageHiveVehicleSchema,
  manualVehicleSchema.extend({ registration: z3.string().optional() })
]);
var vehicleLookupRequestSchema = z3.object({
  registration: z3.string().min(1).max(12),
  /** Lets the API apply per-garage rules (sandbox garages use the mock). */
  garageId: z3.string().optional()
});
var vehicleLookupResultSchema = z3.discriminatedUnion("status", [
  z3.object({ status: z3.literal("found"), vehicle: vehicleSchema }),
  z3.object({ status: z3.literal("not_found"), registration: z3.string() })
]);
function normaliseRegistration(reg) {
  return reg.replace(/\s+/g, "").toUpperCase();
}
function formatRegistration(reg) {
  const r = normaliseRegistration(reg);
  if (r.length === 7) return `${r.slice(0, 4)} ${r.slice(4)}`;
  return r;
}

// src/services.ts
import { z as z4 } from "zod";
var serviceCategorySchema = z4.enum(["core", "additional"]);
var serviceSchema = z4.object({
  id: z4.string(),
  name: z4.string(),
  description: z4.string().optional(),
  /** Price in GBP (pounds, may be fractional). */
  price: z4.number().min(0),
  /** Optional "was" price for strike-through display. */
  priceWas: z4.number().min(0).optional(),
  durationMins: z4.number().int().min(0),
  category: serviceCategorySchema,
  /** Marks the garage's MOT product — the primary service the flow books. */
  isMot: z4.boolean().default(false),
  /** Whether this can be combined with other services in one booking. */
  combinable: z4.boolean().default(true),
  popular: z4.boolean().optional(),
  order: z4.number().int().default(0),
  /** Lucide-style icon key for the UI (e.g. "wrench", "shield-check"). */
  icon: z4.string().optional(),
  /** Garage Hive Online Booking service_price_id / add-on id. */
  garageHiveServicePriceId: z4.number().int().positive().optional(),
  /** True only when this mapping comes from list-additional-services. */
  garageHiveAdditionalService: z4.boolean().optional()
});
var garageHiveOptionsRequestSchema = z4.object({
  branchId: z4.string(),
  vehicleRegistration: z4.string().min(1).max(12),
  serviceIds: z4.array(z4.string()).default([])
});
var garageHiveOptionsResponseSchema = z4.object({
  vehicle: z4.object({
    registration: z4.string().min(1),
    make: z4.string().min(1),
    model: z4.string()
  }),
  services: z4.array(serviceSchema)
});

// src/branch.ts
import { z as z5 } from "zod";
var branchSchema = z5.object({
  id: z5.string(),
  name: z5.string(),
  addressLine: z5.string(),
  postcode: z5.string(),
  lat: z5.number(),
  lng: z5.number(),
  phone: z5.string().optional(),
  /** Garage Hive Online Booking location_id for live booking calls. */
  garageHiveLocationId: z5.number().int().positive().optional(),
  /** Populated when branches are requested with a postcode/location. */
  distanceMiles: z5.number().optional()
});
var branchesRequestSchema = z5.object({
  postcode: z5.string().optional(),
  lat: z5.number().optional(),
  lng: z5.number().optional()
});

// src/availability.ts
import { z as z6 } from "zod";
var timeSlotSchema = z6.object({
  id: z6.string(),
  start: z6.string(),
  // ISO datetime
  end: z6.string(),
  // ISO datetime
  label: z6.string(),
  // e.g. "10:00 – 12:30"
  available: z6.boolean()
});
var availabilityDaySchema = z6.object({
  date: z6.string(),
  // YYYY-MM-DD
  slots: z6.array(timeSlotSchema),
  hasAvailability: z6.boolean()
});
var contactInfoFieldsSchema = z6.object({
  required: z6.array(z6.string()),
  optional: z6.array(z6.string())
});
var availabilityRequestSchema = z6.object({
  branchId: z6.string(),
  serviceIds: z6.array(z6.string()).min(1).refine((ids) => new Set(ids).size === ids.length, {
    message: "serviceIds must not contain duplicates"
  }),
  /** Vehicle registration required by Garage Hive before listing timeslots. */
  vehicleRegistration: z6.string().min(1).optional(),
  /** Registration country code for Garage Hive. Defaults to GB server-side. */
  regNoCountry: z6.string().min(2).max(3).optional(),
  /** YYYY-MM-DD to start from; defaults to today. */
  fromDate: z6.string().optional(),
  /** How many days forward to return. Supports a full year plus leap-day headroom. */
  days: z6.number().int().min(1).max(366).optional()
});
var availabilityResponseSchema = z6.object({
  branchId: z6.string(),
  serviceIds: z6.array(z6.string()),
  days: z6.array(availabilityDaySchema),
  totalDurationMins: z6.number().int(),
  /** Garage Hive fields requested after choosing a timeslot. */
  contactInfoFields: contactInfoFieldsSchema.optional()
});

// src/contact.ts
import { z as z7 } from "zod";
var contactSchema = z7.object({
  name: z7.string().optional(),
  email: z7.string().email().optional(),
  phone: z7.string().min(5).optional()
});
var stepIdSchema = z7.enum([
  "registration",
  "vehicle",
  "location",
  "contact",
  "mot",
  "services",
  "datetime",
  "details",
  "summary",
  "confirmation"
]);
var leadStatusSchema = z7.enum(["active", "abandoned", "converted"]);
var leadInputSchema = z7.object({
  garageId: z7.string(),
  sessionId: z7.string(),
  contact: contactSchema,
  vehicle: anyVehicleSchema.optional(),
  postcode: z7.string().optional(),
  branchId: z7.string().optional(),
  serviceIds: z7.array(z7.string()).optional(),
  lastStep: stepIdSchema
});
var leadUpdateSchema = leadInputSchema.partial().extend({ status: leadStatusSchema.optional() });
var leadSchema = leadInputSchema.extend({
  id: z7.string(),
  status: leadStatusSchema,
  sandbox: z7.boolean(),
  createdAt: z7.string(),
  updatedAt: z7.string()
});

// src/config.ts
import { z as z8 } from "zod";
var contactStrategySchema = z8.enum([
  "email",
  "phone",
  "either",
  "both"
]);
var confirmationChannelSchema = z8.enum(["email", "sms", "both"]);
var DEFAULT_STEPS = [
  "registration",
  "vehicle",
  "location",
  "mot",
  "services",
  "datetime",
  "details",
  "summary",
  "confirmation"
];
var flowConfigSchema = z8.object({
  steps: z8.array(stepIdSchema).default([...DEFAULT_STEPS]),
  contactStrategy: contactStrategySchema.default("either"),
  /** Legacy setting retained for stored configs; contact is collected in details. */
  contactFirst: z8.boolean().default(false),
  /** If true, branch/location selection happens before registration. */
  branchFirst: z8.boolean().default(false),
  /**
   * Let the customer choose a location. Fixed-location widgets are the default
   * and use the first configured branch; selectable widgets must opt in and
   * have more than one branch.
   */
  locationEnabled: z8.boolean().default(false),
  confirmationChannel: confirmationChannelSchema.default("email")
});
var sessionTimerConfigSchema = z8.object({
  enabled: z8.boolean().default(false),
  minutes: z8.number().int().min(1).max(60).default(15)
});
var trustConfigSchema = z8.object({
  enabled: z8.boolean().default(true),
  provider: z8.literal("trustpilot").default("trustpilot"),
  rating: z8.number().min(0).max(5).optional(),
  reviewCount: z8.number().int().min(0).optional(),
  label: z8.string().optional()
});
var garageConfigSchema = z8.object({
  id: z8.string(),
  name: z8.string(),
  slug: z8.string(),
  /** Demo/test instance — analytics tagged sandbox, never hits a real GarageHive. */
  sandbox: z8.boolean().default(false),
  theme: themeConfigSchema,
  copy: copyConfigSchema,
  flow: flowConfigSchema,
  sessionTimer: sessionTimerConfigSchema,
  trust: trustConfigSchema,
  branches: z8.array(branchSchema),
  services: z8.array(serviceSchema)
});
function resolveFlowSteps(config) {
  let steps = config.flow.steps.filter((step) => step !== "contact");
  if (config.flow.steps.includes("contact") && !steps.includes("details")) {
    const summaryIndex = steps.indexOf("summary");
    steps.splice(summaryIndex >= 0 ? summaryIndex : steps.length, 0, "details");
  }
  const locationSelectable = config.flow.locationEnabled && config.branches.length > 1;
  if (!locationSelectable) {
    steps = steps.filter((s) => s !== "location");
  }
  const hasMotService = config.services.some((s) => s.isMot);
  if (!hasMotService) {
    steps = steps.filter((s) => s !== "mot");
  }
  return steps;
}
var garageSummarySchema = z8.object({
  id: z8.string(),
  name: z8.string(),
  slug: z8.string(),
  sandbox: z8.boolean(),
  branchCount: z8.number().int(),
  serviceCount: z8.number().int()
});

// src/booking.ts
import { z as z9 } from "zod";
var bookingStatusSchema = z9.enum(["confirmed", "draft", "cancelled"]);
var bookingInputSchema = z9.object({
  garageId: z9.string(),
  branchId: z9.string(),
  sessionId: z9.string(),
  vehicle: anyVehicleSchema,
  vehicleRegistration: z9.string().optional(),
  /** Name is required to confirm a booking; email/phone per garage strategy. */
  contact: contactSchema.extend({ name: z9.string().min(1) }),
  serviceIds: z9.array(z9.string()).min(1).refine((ids) => new Set(ids).size === ids.length, {
    message: "serviceIds must not contain duplicates"
  }),
  slotId: z9.string(),
  slotStart: z9.string(),
  // ISO
  /** Optional final contact fields used by Garage Hive Online Booking. */
  contactAddress: z9.string().optional(),
  contactCity: z9.string().optional(),
  contactPostcode: z9.string().optional(),
  notes: z9.string().optional()
});
var bookingSchema = bookingInputSchema.extend({
  id: z9.string(),
  reference: z9.string(),
  status: bookingStatusSchema,
  totalPrice: z9.number(),
  totalDurationMins: z9.number().int(),
  createdAt: z9.string(),
  confirmationSentVia: confirmationChannelSchema.nullable(),
  externalRef: z9.string().nullable(),
  sandbox: z9.boolean()
});
function generateBookingReference(rand = Math.random) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 6; i++) {
    out += alphabet[Math.floor(rand() * alphabet.length)];
  }
  return `AP-${out}`;
}

// src/analytics.ts
import { z as z10 } from "zod";
var analyticsEventSchema = z10.enum([
  "widget_loaded",
  "registration_entered",
  "vehicle_found",
  "vehicle_not_found",
  "manual_vehicle_entered",
  "location_entered",
  "lead_captured",
  "service_selected",
  "datetime_selected",
  "details_entered",
  "summary_viewed",
  "no_slots_shown",
  "booking_completed",
  "booking_abandoned"
]);
var analyticsEventInputSchema = z10.object({
  garageId: z10.string(),
  sessionId: z10.string(),
  event: analyticsEventSchema,
  properties: z10.record(z10.unknown()).optional(),
  sandbox: z10.boolean().default(false),
  ts: z10.string().optional()
});
var FUNNEL_STAGES = [
  "widget_loaded",
  "registration_entered",
  "vehicle_found",
  "location_entered",
  "lead_captured",
  "service_selected",
  "datetime_selected",
  "details_entered",
  "booking_completed"
];
var funnelStageStatSchema = z10.object({
  event: analyticsEventSchema,
  label: z10.string(),
  sessions: z10.number().int(),
  /** Drop-off from the previous stage, 0..1. */
  dropOff: z10.number()
});
var volumePointSchema = z10.object({
  date: z10.string(),
  // YYYY-MM-DD
  bookings: z10.number().int(),
  leads: z10.number().int()
});
var serviceStatSchema = z10.object({
  serviceId: z10.string(),
  name: z10.string(),
  bookings: z10.number().int(),
  revenue: z10.number()
});
var branchStatSchema = z10.object({
  branchId: z10.string(),
  name: z10.string(),
  bookings: z10.number().int(),
  conversion: z10.number()
  // 0..1
});
var analyticsSummarySchema = z10.object({
  range: z10.object({ from: z10.string(), to: z10.string() }),
  includeSandbox: z10.boolean(),
  totals: z10.object({
    sessions: z10.number().int(),
    bookings: z10.number().int(),
    leads: z10.number().int(),
    abandoned: z10.number().int(),
    revenue: z10.number(),
    conversion: z10.number()
    // 0..1 sessions -> bookings
  }),
  funnel: z10.array(funnelStageStatSchema),
  volume: z10.array(volumePointSchema),
  topServices: z10.array(serviceStatSchema),
  branches: z10.array(branchStatSchema)
});

// src/post-message.ts
import { z as z11 } from "zod";
var PROTOCOL_VERSION = 1;
var base = { v: z11.literal(PROTOCOL_VERSION) };
var iframeReadyMessage = z11.object({
  type: z11.literal("autopic:ready"),
  ...base
});
var resizeMessage = z11.object({
  type: z11.literal("autopic:resize"),
  ...base,
  height: z11.number()
});
var bookingCompleteMessage = z11.object({
  type: z11.literal("autopic:booking-complete"),
  ...base,
  reference: z11.string(),
  garageId: z11.string()
});
var tyreOrderCompleteMessage = z11.object({
  type: z11.literal("autopic:tyre-order-complete"),
  ...base,
  reference: z11.string(),
  garageId: z11.string(),
  environment: z11.enum(["test", "demo"])
});
var openPopupMessage = z11.object({
  type: z11.literal("autopic:open-popup"),
  ...base
});
var closePopupMessage = z11.object({
  type: z11.literal("autopic:close-popup"),
  ...base
});
var iframeToLoaderMessage = z11.discriminatedUnion("type", [
  iframeReadyMessage,
  resizeMessage,
  bookingCompleteMessage,
  tyreOrderCompleteMessage,
  openPopupMessage,
  closePopupMessage
]);
var initMessage = z11.object({
  type: z11.literal("autopic:init"),
  ...base,
  garageId: z11.string(),
  mode: z11.enum(["inline", "popup"])
});
var loaderToIframeMessage = z11.discriminatedUnion("type", [
  initMessage
]);
function isAutopicMessage(data) {
  return typeof data === "object" && data !== null && "type" in data && typeof data.type === "string" && data.type.startsWith("autopic:");
}

// src/helpers.ts
function formatGBP(amount) {
  const hasPence = Math.round(amount * 100) % 100 !== 0;
  return `\xA3${amount.toLocaleString("en-GB", {
    minimumFractionDigits: hasPence ? 2 : 0,
    maximumFractionDigits: 2
  })}`;
}
function computeTotals(services, selectedIds) {
  const selected = services.filter((s) => selectedIds.includes(s.id));
  return {
    totalPrice: selected.reduce((sum, s) => sum + s.price, 0),
    totalDurationMins: selected.reduce((sum, s) => sum + s.durationMins, 0)
  };
}
function distanceMiles(a, b) {
  const R = 3958.8;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return Math.round(2 * R * Math.asin(Math.sqrt(h)) * 10) / 10;
}
function isContactSatisfied(strategy, contact) {
  const hasEmail = !!contact.email;
  const hasPhone = !!contact.phone;
  switch (strategy) {
    case "email":
      return hasEmail;
    case "phone":
      return hasPhone;
    case "either":
      return hasEmail || hasPhone;
    case "both":
      return hasEmail && hasPhone;
  }
}
function generateSessionId(rand = Math.random) {
  return "s_" + Date.now().toString(36) + "_" + Math.floor(rand() * 1e9).toString(36);
}

// src/auth.ts
var ADMIN_TOKEN_STORAGE_KEY = "autopic.admin.token";

// src/tyres.ts
import { z as z12 } from "zod";
var tyreSearchSchema = z12.object({
  size: z12.string().trim().toUpperCase().transform((s) => s.replace(/[\s/R]/g, "")).pipe(z12.string().regex(/^\d{7}$/, "Enter a tyre size such as 205/55 R16")),
  category: z12.enum(["all", "Budget", "MidRange", "Premium"]).default("all"),
  season: z12.enum(["all", "Summer", "Winter", "AllSeason"]).default("all"),
  page: z12.number().int().min(1).max(100).default(1)
});
var tyreSelectionSchema = z12.object({
  productCode: z12.string().min(1).max(80).regex(/^[a-zA-Z0-9._/-]+$/),
  quantity: z12.number().int().min(1).max(4)
});
var tyreImagePathSchema = z12.string().trim().max(200).regex(/^[a-zA-Z0-9 &()+_-]+\/[a-zA-Z0-9 &()+,._-]+\.(?:jpe?g|png|webp)$/i).refine((path) => !path.includes("..")).transform((path) => {
  const [brand, filename] = path.split("/");
  return `${brand}/${filename.toLowerCase()}`;
});
function tyreImageUrl(value) {
  const path = tyreImagePathSchema.safeParse(value);
  return path.success ? `/api/tyres/images?path=${encodeURIComponent(path.data)}` : null;
}
function choiceScore(seed, code) {
  let hash = 2166136261;
  for (const char of `${seed}|${code}`) {
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  }
  return hash >>> 0;
}
function paginateTyreProducts(catalogue, page, pageSize, seed) {
  const available = catalogue.filter((p) => p.stock > 0 && p.deliveryDate);
  const prices = [...new Set(available.map((p) => p.unitPricePence))].sort(
    (a, b) => a - b
  );
  const lowerEnd = Math.ceil(prices.length / 3);
  const higherStart = Math.ceil(prices.length * 2 / 3);
  const bands = [
    prices.slice(higherStart),
    prices.slice(lowerEnd, higherStart),
    prices.slice(0, lowerEnd)
  ];
  const featured = bands.flatMap((band) => {
    const candidates = available.filter((p) => band.includes(p.unitPricePence));
    const enoughStock = candidates.filter((p) => p.stock >= 4);
    const stocked = enoughStock.length ? enoughStock : candidates;
    const pictured = stocked.filter((p) => p.imageUrl);
    const pool = pictured.length ? pictured : stocked;
    const choice = [...pool].sort(
      (a, b) => choiceScore(seed, a.code) - choiceScore(seed, b.code) || a.code.localeCompare(b.code)
    )[0];
    return choice ? [choice] : [];
  });
  const featuredCodes = new Set(featured.map((p) => p.code));
  const remaining = catalogue.filter((p) => !featuredCodes.has(p.code));
  return {
    total: catalogue.length,
    featured,
    remainingTotal: remaining.length,
    products: remaining.slice((page - 1) * pageSize, page * pageSize),
    page,
    pageSize
  };
}
var tyreQuoteDetailsSchema = z12.object({
  productCode: z12.string(),
  description: z12.string(),
  quantity: z12.number().int().positive(),
  unitPricePence: z12.number().int().nonnegative(),
  deliveryDate: z12.string(),
  tradingPoint: z12.string()
});
var tyreOrderResultSchema = z12.object({
  environment: z12.literal("test"),
  reference: z12.string(),
  bondOrderNumber: z12.string(),
  description: z12.string(),
  quantity: z12.number().int().positive(),
  netPence: z12.number().int().nonnegative(),
  vatPence: z12.number().int().nonnegative(),
  deliveryDate: z12.string().nullable(),
  deliveryDateChanged: z12.boolean()
});
export {
  ADMIN_TOKEN_STORAGE_KEY,
  DEFAULT_COPY,
  DEFAULT_STEPS,
  DEFAULT_THEME,
  DESIGN_TOKENS,
  FUNNEL_STAGES,
  PROTOCOL_VERSION,
  analyticsEventInputSchema,
  analyticsEventSchema,
  analyticsSummarySchema,
  anyVehicleSchema,
  availabilityDaySchema,
  availabilityRequestSchema,
  availabilityResponseSchema,
  bookingCompleteMessage,
  bookingInputSchema,
  bookingSchema,
  bookingStatusSchema,
  branchSchema,
  branchStatSchema,
  branchesRequestSchema,
  closePopupMessage,
  computeTotals,
  confirmationChannelSchema,
  contactInfoFieldsSchema,
  contactSchema,
  contactStrategySchema,
  copyConfigSchema,
  distanceMiles,
  flowConfigSchema,
  formatGBP,
  formatRegistration,
  fuelTypeSchema,
  funnelStageStatSchema,
  garageConfigSchema,
  garageHiveOptionsRequestSchema,
  garageHiveOptionsResponseSchema,
  garageHiveVehicleSchema,
  garageSummarySchema,
  generateBookingReference,
  generateSessionId,
  iframeReadyMessage,
  iframeToLoaderMessage,
  initMessage,
  isAutopicMessage,
  isContactSatisfied,
  leadInputSchema,
  leadSchema,
  leadStatusSchema,
  leadUpdateSchema,
  loaderToIframeMessage,
  manualVehicleSchema,
  normaliseRegistration,
  openPopupMessage,
  paginateTyreProducts,
  resizeMessage,
  resolveFlowSteps,
  serviceCategorySchema,
  serviceSchema,
  serviceStatSchema,
  sessionTimerConfigSchema,
  stepIdSchema,
  themeConfigSchema,
  timeSlotSchema,
  trustConfigSchema,
  tyreImagePathSchema,
  tyreImageUrl,
  tyreOrderCompleteMessage,
  tyreOrderResultSchema,
  tyreQuoteDetailsSchema,
  tyreSearchSchema,
  tyreSelectionSchema,
  vehicleLookupRequestSchema,
  vehicleLookupResultSchema,
  vehicleSchema,
  volumePointSchema
};
//# sourceMappingURL=index.js.map